import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import { now, user } from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { animDuration, delay, waitForItPromise } from 'src/lib/utils';
import {
  getSuperLollipopWinStreak,
  getSuperLollipopWinStreakRequired,
  isSuperLollipopActive,
  isSuperLollipopAvailable,
} from 'src/replicant/superLollipop/superLollipop.getters';
import { SuperLollipopIcon } from '../components/SuperLollipopIcon';
import Match2TournamentStreakBar from 'src/game/minigame/match2-timestep/tournament/Match2TournamentStreakBar';
import {
  getMatch2CloseButtonX,
  match2CloseButtonY,
} from 'src/game/minigame/match2-timestep/components/match2PopupCloseButton';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import * as match2TournamentGetters from 'src/replicant/match2/tournament/match2-tournament.getters';
import { SuperLollipopLosing } from '../components/SuperLollipopLosing';
import { GemsWallet } from 'src/game/components/GemsWallet';
import { movesPinchCheck } from 'src/lib/pinch';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupSuperLollipopPinchRetry extends PopupBasicLazy {
  private close: CloseHandler;
  private contentWrapper: View;
  private ctaButton: ButtonScaleViewWithText;
  private pet: PetEntity;
  private species: PetSpeciesID;
  private achievements: FlexBoxView;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;
  private bubble: ImageScaleView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = 676;
    const height = 900;
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      darkerBg: true,
      offsetY: -50,
      width,
      height,
      zIndex: 10000,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.title.updateOpts({ color: '#E25959' });
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.95)',
    });
    this.bg.onClick = () => this.toggleShowPuzzle();

    new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    new ButtonScaleView({
      ...uiConfig.buttons.close,
      superview: this.box,
      x: getMatch2CloseButtonX(this.box.style.width),
      y: match2CloseButtonY,
      onClick: async () => {
        sounds.playSound('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_BACK,
      },
    });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    this.species = Pet.getters.getCurrentEvolution(user())?.species ?? 'ball';
    this.pet = new PetEntity('minigame', this.species);
    this.pet.updateOpts({
      superview: this.contentWrapper,
      x: 145,
      y: 300,
      anchorX: 0.5 * this.pet.style.width,
      anchorY: 0.78 * this.pet.style.height,
    });

    // bubble speech
    this.bubble = new ImageScaleView({
      superview: this.contentWrapper,
      image: `assets/ui/conversation/speechbubble.png`,
      imgScale: 0.66,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 44, right: 44 },
        vertical: { top: 44, bottom: 44 },
      },
      width: 360,
      height: 200,
      x: 280,
      y: 100,
    });

    new ImageView({
      superview: this.bubble,
      image: 'assets/ui/conversation/speechbubble_tail.png',
      width: 82,
      height: 67,
      y: 75,
      x: 10,
      r: Math.PI / 2,
      scale: 0.5,
    });

    const superLollipopActive = isSuperLollipopActive(user());
    const bubbleTextKey = superLollipopActive
      ? 'superLollipop.popupPinch.dontLose'
      : 'match2.movesPinch.dontLose';
    new LangBitmapFontTextView({
      superview: this.bubble,
      size: 32,
      font: 'Body',
      color: '#985C25',
      localeText: () => i18n(bubbleTextKey),
      width: this.bubble.style.width,
      height: this.bubble.style.height,
      x: 0,
      y: -5,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
    });

    this.achievements = new FlexBoxImageScaleView({
      superview: this.contentWrapper,
      width: 540,
      height: 390,
      image: 'assets/ui/match2/common/list_scroll_area.png',
      flex: {
        flexDirection: 'column',
        alignContent: 'center',
        justifyContent: 'center',
      },
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 30, right: 30 },
        vertical: { top: 30, middle: 30, bottom: 30 },
      },
      imgScale: 2,
      x: 75,
      y: 340,
    });

    this.ctaButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.contentWrapper,
      width: 477,
      height: 110,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height - 100,
      fontSize: 50,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 54,
        height: 45,
        offset: { x: 190, y: 0 },
      },
      labelOffsetX: -35,
      localeText: () => i18n('match2.movesPinch.cta'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_OK,
      },
    });

    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 150 + this.box.style.y + this.box.style.height * 0.5,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      ...SuperLollipopIcon.assets(),
      ...AssetGroup.constructURLs(['assets/ui/superLollipop/shared']),
    ]);
  }

  public init({ price }: { price: number }) {
    super.init({ title: i18n(`match2.movesPinch.title3`) });

    const petAnimations = petAnimationNames[this.species];
    this.pet.setAnimation(petAnimations.cry);

    this.ctaButton.label.localeText = () =>
      `${i18n('match2.movesPinch.cta')}  ${price}`;
    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');
      const success = await movesPinchCheck(price, 'match2_shop_moves');
      if (success) this.close(true);
    };
  }

  private addAchievements() {
    const achievements: {
      view: View;
      lossAmount: number;
      animate: () => void;
    }[] = [];

    const lollipopItem = this.getSuperLollipopItem();
    lollipopItem && achievements.push(lollipopItem);

    const tournamentItem = this.getTournamentItem();
    tournamentItem && achievements.push(tournamentItem);

    // Add achievements to the view in order and animate them.
    achievements.forEach((achievement, index) => {
      const { view, animate } = achievement;
      view.updateOpts({
        superview: this.achievements,
      });

      waitForItPromise(1000 + 500 * index).then(() => {
        animate();
      });
    });

    // If no achievements, hide the achievements container, the bubble and center pet.
    if (!achievements.length) {
      const achievementsHeight = this.achievements.style.height;
      this.box.style.height -= achievementsHeight;
      this.box.style.y += achievementsHeight * 0.5;
      this.ctaButton.style.y -= achievementsHeight;
      this.achievements.removeFromSuperview();
      this.bubble?.removeFromSuperview();
      this.pet.updateOpts({
        x: this.contentWrapper.style.width * 0.5,
      });
    }
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    await super.fadeIn();
    this.addAchievements();
    await delay(animDuration * 2);
    await this.tweenToggleMessageScaleY(1);
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
  }

  private getSuperLollipopItem() {
    const state = user();
    if (!isSuperLollipopAvailable(state)) {
      return null;
    }

    const currentProgress = getSuperLollipopWinStreak(user()) + 1;
    const maxProgress = getSuperLollipopWinStreakRequired(state);

    const superLollipopLosing = new SuperLollipopLosing({
      currentProgress: currentProgress,
      maxProgress: getSuperLollipopWinStreakRequired(state),
    });

    return {
      view: superLollipopLosing,
      lossAmount: currentProgress / maxProgress,
      animate: () => superLollipopLosing.animate(),
    };
  }

  private getTournamentItem() {
    const tournamentMultiplierIndex =
      match2TournamentGetters.getTournamenCurrentMultiplierIndex(user(), now());
    if (tournamentMultiplierIndex <= 0) return null;

    const container = new View({
      width: 540,
      height: 140,
    });
    const tournamentBar = new Match2TournamentStreakBar({
      superview: container,
      width: 400,
      centerOnOrigin: false,
      x: 80,
      y: 80,
    });
    tournamentBar.on('ViewRemoved', () => {
      tournamentBar.unflash();
    });
    new LangBitmapFontTextShadowView({
      superview: container,
      localeText: () => i18n('superLollipop.popupPinch.tournament'),
      color: '#985C25',
      font: 'Body',
      size: 24,
      align: 'center',
      verticalAlign: 'center',
      width: 540,
      height: 40,
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      textShadowColor: '#FFFFFF',
    });

    return {
      view: container,
      lossAmount: 0,
      animate: () => tournamentBar.flash(),
    };
  }
}
